/**
 * <product-card-switcher>
 * Rendered by snippets/product-card-slider-card.liquid when `switch_swatches`
 * is on; loaded by sections/product-card-slider.liquid,
 * sections/pdp-also-like.liquid and sections/collection-community-choice.liquid.
 *
 * Clicking a swatch repaints the card as that colour — photo, title, price,
 * compare-at price, add-to-cart and both links — without a page load. Clicking
 * the card itself still navigates, to whichever colour is currently showing.
 *
 * WHY THE WHOLE CARD MOVES, NOT JUST THE PHOTO
 *
 * On this store a colour is a separate PRODUCT, not a variant: siblings are
 * linked by a shared `custom.variant_color` metaobject, and the chips are those
 * siblings. A gold photo over the silver product's price, linking to the silver
 * page, would be wrong on all three counts — so everything moves together or
 * nothing does. The write-up is .claude/features/color-material-swatches/
 * feature.md; `data-pcs-mode="variants"` is the other path, where a chip really
 * is a variant of one product and the title stays put.
 *
 * NOTHING IS FETCHED. Every colour's images and copy are already in the page,
 * rendered server-side — the groups are `hidden`, not absent. A click is a class
 * toggle and a few text writes, so it is instant and cannot fail offline. The
 * cost is page weight, which is why the card only renders a full gallery per
 * colour when the hover arrows exist to page through one.
 *
 * INDEX COUPLING: a chip's `data-pcs-index` is matched against a group's
 * `data-pcs-group-index` BY VALUE. Never rewrite either to use DOM position —
 * the optional `self` group has no chip, and a sibling that resolves to no
 * colour and no image emits no chip. See the note in the card snippet.
 */
if (!customElements.get('product-card-switcher')) {
  customElements.define(
    'product-card-switcher',
    class ProductCardSwitcher extends HTMLElement {
      connectedCallback() {
        this.swatches = Array.from(this.querySelectorAll('[data-pcs-swatch]'));
        this.groups = Array.from(this.querySelectorAll('[data-pcs-shot-group]'));
        if (this.swatches.length === 0 || this.groups.length === 0) return;

        this.links = Array.from(this.querySelectorAll('[data-pcs-link]'));
        this.titleEl = this.querySelector('[data-pcs-title]');
        this.priceEl = this.querySelector('[data-pcs-price]');
        this.wasEl = this.querySelector('[data-pcs-was]');
        this.variantInput = this.querySelector('[data-pcs-variant-input]');
        this.atcBtn = this.querySelector('[data-pcs-atc]');
        this.gallery = this.querySelector('product-card-gallery');

        // The state the card was rendered in, so clicking the active chip a
        // second time is a no-op rather than a re-run.
        this.current = this.querySelector('[data-pcs-shot-group]:not([hidden])');

        this.handleClick = this.onClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      /**
       * Delegated from the card, so the listener survives any chip being
       * re-rendered and costs one binding per card rather than one per chip.
       * @param {object} evt - Click event.
       */
      onClick(evt) {
        const swatch = evt.target.closest('[data-pcs-swatch]');
        if (!swatch || !this.contains(swatch)) return;

        // The chips sit outside the card's links, so nothing would navigate
        // anyway — but they are inside <product-card-gallery>'s subtree and
        // inside <product-form>, and a bare <button> in a form submits it.
        evt.preventDefault();
        evt.stopPropagation();

        this.select(swatch);
      }

      /**
       * Repaints the card as the colour a chip describes.
       * @param {HTMLElement} swatch - The clicked chip.
       */
      select(swatch) {
        const group = this.groups.find(
          (el) => el.dataset.pcsGroupIndex === swatch.dataset.pcsIndex
        );

        // No group for this chip means the two Liquid loops have drifted apart.
        // Bail rather than half-applying: a card showing silver under a gold
        // chip, at the gold price, is worse than one that ignored the click.
        if (!group) return;

        this.setGroup(group);
        this.setText(swatch);
        this.setLinks(swatch.dataset.pcsUrl);
        this.setAddToCart(swatch);
        this.setPressed(swatch);
      }

      /**
       * @param {HTMLElement} group - The media group to show.
       */
      setGroup(group) {
        if (this.current === group) return;

        this.groups.forEach((el) => {
          const isActive = el === group;
          el.hidden = !isActive;
          el.classList.toggle('product-card-slider__shot-group--active', isActive);
        });
        this.current = group;

        // The arrows belong to the gallery component, and the colour just
        // selected may have one image where the last had six — so the gallery
        // re-reads its shots and hides its own nav if there is nothing to page.
        if (this.gallery && typeof this.gallery.useGroup === 'function') {
          this.gallery.useGroup(group);
        }
      }

      /**
       * Title, price and compare-at price.
       *
       * innerHTML rather than textContent because `money` output follows the
       * shop's money format, which can legitimately carry markup. The value is
       * Liquid's own, escaped into the attribute and un-escaped by the browser
       * on read — it never came from a shopper.
       *
       * @param {HTMLElement} swatch - The clicked chip.
       */
      setText(swatch) {
        const { pcsTitle, pcsPrice, pcsCompare } = swatch.dataset;

        // Absent on the variants path: one product, one title.
        if (this.titleEl && pcsTitle) this.titleEl.textContent = pcsTitle;
        if (this.priceEl && pcsPrice) this.priceEl.innerHTML = pcsPrice;

        if (this.wasEl) {
          const hasCompare = Boolean(pcsCompare);
          this.wasEl.innerHTML = hasCompare ? pcsCompare : '';
          this.wasEl.hidden = !hasCompare;
        }
      }

      /**
       * @param {string} url - Where the card should now point.
       */
      setLinks(url) {
        if (!url) return;
        this.links.forEach((link) => {
          link.href = url;
        });
      }

      /**
       * The hidden variant id the add-to-cart form posts, plus the button's
       * label and disabled state. Both translations ship on the button, so this
       * needs no global strings table.
       *
       * Symmetry's <product-form> reads the form at submit time and keys off
       * `[name="add"]`, not off any cached node, so swapping the input's value
       * underneath it is all that is required.
       *
       * @param {HTMLElement} swatch - The clicked chip.
       */
      setAddToCart(swatch) {
        const { pcsVariant, pcsAvailable } = swatch.dataset;
        if (this.variantInput && pcsVariant) this.variantInput.value = pcsVariant;
        if (!this.atcBtn) return;

        const available = pcsAvailable === 'true';
        this.atcBtn.disabled = !available;
        const label = available
          ? this.atcBtn.dataset.pcsLabelAdd
          : this.atcBtn.dataset.pcsLabelSold;
        if (label) this.atcBtn.textContent = label;
      }

      /**
       * @param {HTMLElement} swatch - The chip to mark as selected.
       */
      setPressed(swatch) {
        this.swatches.forEach((el) => {
          const isCurrent = el === swatch;
          el.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
          el.classList.toggle('product-card-slider__swatch--current', isCurrent);
        });
      }
    }
  );
}
